import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight, Smartphone, RotateCcw, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import "../../Styles/Admin/UserTable.css";
import { API_BASE_URL } from "../../apiConfig";
import { getAllUsersWithDevices, resetUserDeviceByEmployeeId } from "../../services/firebaseService";
import Toast from "../Common/Toast";

const UserTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [deviceData, setDeviceData] = useState({});
  const [resetStatus, setResetStatus] = useState({});
  const [toast, setToast] = useState({ message: "", type: "" });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

 

  useEffect(() => {
    fetchUsers();
    fetchDeviceData();
  }, []);

  const fetchUsers = async () => {
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });
      const data = await response.json();
      if (data.success) {
     
        setUsers(data.users);
        setFilteredUsers(data.users);
      } else {
        console.error("Failed to fetch users:", data.message);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchDeviceData = async () => {
    
    try {
      const usersWithDevices = await getAllUsersWithDevices();
     
      setDeviceData(usersWithDevices);
    } catch (error) {
      console.error('👥 UserTable: Error fetching device data:', error);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = users.filter(
      (user) => user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term)
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleDeviceReset = async (user) => {
    if (!user.employeeId) {
      setToast({ message: "Employee ID not found for this user", type: "error" });
      return;
    }

   
    
    try {
      setResetStatus({ ...resetStatus, [user.id]: { loading: true } });
      
      const success = await resetUserDeviceByEmployeeId(user.employeeId);
      
      if (success) {
        setResetStatus({ ...resetStatus, [user.id]: { success: true } });
        setToast({ 
          message: `Device reset successful for ${user.name}`, 
          type: "success" 
        });
        
        // Refresh device data
        setTimeout(() => {
          fetchDeviceData();
          setResetStatus({ ...resetStatus, [user.id]: null });
        }, 2000);
      } else {
        setResetStatus({ ...resetStatus, [user.id]: { error: 'Failed to reset device' } });
        setToast({ 
          message: `Failed to reset device for ${user.name}`, 
          type: "error" 
        });
      }
    } catch (error) {
      setResetStatus({ ...resetStatus, [user.id]: { error: error.message } });
      setToast({ 
        message: `Error resetting device for ${user.name}: ${error.message}`, 
        type: "error" 
      });
    }
  };

  const getDeviceStatus = (user) => {
   
    
    if (user.role === 'admin' || user.role === 'Superadmin') {
      return { status: 'N/A', color: 'not-applicable-text', hasDevice: false };
    }

    const userDeviceData = deviceData[user.firebaseUid] || deviceData[user.id];
    
    
    if (!userDeviceData) {
      return { status: 'No Device', color: 'device-gray', hasDevice: false };
    }

    if (userDeviceData.hasDevice && userDeviceData.deviceId) {
      return { status: 'Registered', color: 'device-green', hasDevice: true };
    } else if (userDeviceData.resetAt) {
      return { status: 'Reset', color: 'device-orange', hasDevice: false };
    } else {
      return { status: 'No Device', color: 'device-gray', hasDevice: false };
    }
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  

  return (
    <motion.div
      className="users-table-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Toast
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ message: "", type: "" })}
      />
      
      <div className="users-table-header">
        <h2 className="users-table-title">Users & Device Management</h2>
        <div className="users-table-search-container">
          <input
            type="text"
            placeholder="Search users..."
            className="users-table-search-input"
            value={searchTerm}
            onChange={handleSearch}
          />
          <Search className="users-table-search-icon" size={18} />
        </div>
      </div>

      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Device Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => {
              const deviceStatus = getDeviceStatus(user);
              const isResetting = resetStatus[user.id]?.loading;
              const resetSuccess = resetStatus[user.id]?.success;
              const resetError = resetStatus[user.id]?.error;
              
              return (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <td>
                    <div className="user-name-cell">
                      <div className="user-avatar">{user.name.charAt(0)}</div>
                      <div className="user-name">{user.name}</div>
                    </div>
                  </td>
                  <td>
                    <div className="user-email">{user.email}</div>
                  </td>
                  <td>
                    <span className="user-role">{user.role}</span>
                  </td>
                  <td>
                    <span
                      className={`user-status ${
                        user.status === "active" ? "status-active" : "status-inactive"
                      }`}
                    >
                      {user.status === "active" ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td>
                    <div className="device-status-cell">
                      <Smartphone className="device-icon" size={16} />
                      {user.role === 'admin' || user.role === 'Superadmin' ? (
                        <span className="not-applicable-text">N/A</span>
                      ) : (
                        <span className={`device-status ${deviceStatus.color}`}>
                          {deviceStatus.status}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {user.role === 'user' && (
                        <button
                          className={`reset-device-btn ${
                            isResetting ? 'loading' : resetSuccess ? 'success' : ''
                          }`}
                          onClick={() => handleDeviceReset(user)}
                          disabled={isResetting}
                        >
                          {isResetting ? (
                            <>
                              <RotateCcw size={12} className="animate-spin" />
                              Resetting...
                            </>
                          ) : resetSuccess ? (
                            <>
                              <Shield size={12} />
                              Reset!
                            </>
                          ) : (
                            <>
                              <RotateCcw size={12} />
                              Reset Device
                            </>
                          )}
                        </button>
                      )}
                      {(user.role === 'admin' || user.role === 'Superadmin') && (
                        <span className="not-applicable-text">No Action Required</span>
                      )}
                      {resetError && (
                        <span className="error-text" style={{ color: '#ef4444', fontSize: '0.75rem' }}>
                          {resetError}
                        </span>
                      )}
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default UserTable;

